import { relaunchApi } from '@/logic/relaunchApi'
import store from '@/store'

// We can only ever have one recording happening at the same time so we can get away with having
// shared variables across the whole feature.
let sessionActive = false
let streamRef = null
let vadInstance = null
let vadAudioContext = null
let vadAudioNodePromise = null
let stopAndTranscribe = null
let mediaRecorder = null
let audioChunks = []
let detectedAudioFormat = null
let nrtVadPromise = null

function detectSupportedAudioFormat() {
  if (detectedAudioFormat) {
    return
  }

  const preferred = [
    { mimeType: 'audio/webm;codecs=opus', extension: 'webm' },
    { mimeType: 'audio/ogg;codecs=opus', extension: 'ogg' },
    { mimeType: 'audio/webm', extension: 'webm' },
    { mimeType: 'audio/ogg', extension: 'ogg' },
    { mimeType: 'audio/mp4;codecs=mp4a', extension: 'mp4' },
    { mimeType: 'audio/mp4', extension: 'mp4' },
  ]

  const format = preferred.find((f) => MediaRecorder.isTypeSupported(f.mimeType))
  if (!format) {
    throw new Error('No supported audio MIME type found')
  }

  detectedAudioFormat = format
}

async function stopRecording() {
  if (!sessionActive) {
    return
  }
  // This frees up resources and stops the "recording" indicator in the browser tab.
  vadInstance?.pause()
  if (streamRef) {
    streamRef.getTracks().forEach((track) => track.stop())
    streamRef = null
  }
  vadInstance = null
  sessionActive = false
}

async function runTranscription(audioBlob) {
  const formData = new FormData()
  formData.append('file', audioBlob, `recording.${detectedAudioFormat.extension}`)
  const response = await relaunchApi.post('/global/speech-services/transcribe', formData)
  return response.data?.transcription
}

// Lazily prepare a NonRealTimeVAD instance for offline analysis of recorded audio
async function prepareNonRealTimeVad() {
  if (!nrtVadPromise) {
    const { NonRealTimeVAD } = await import('@ricky0123/vad-web')
    nrtVadPromise = NonRealTimeVAD.new({
      model: 'legacy',
    })
  }
  return nrtVadPromise
}

// Decode a Blob to mono Float32Array and retrieve its sample rate
async function blobToFloat32AndSampleRate(blob) {
  const arrayBuffer = await blob.arrayBuffer()
  const AudioContextClass = window.AudioContext || window.webkitAudioContext
  if (!AudioContextClass) {
    throw new Error('Web Audio API is not supported in this environment')
  }
  const audioContext = new AudioContextClass()
  try {
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
    const sampleRate = audioBuffer.sampleRate
    // Use first channel; if more channels exist, we can extend to mixdown later
    const source = audioBuffer.numberOfChannels > 0
      ? audioBuffer.getChannelData(0)
      : new Float32Array(0)
    // Copy to detach from internal buffer
    const float32 = new Float32Array(source.length)
    float32.set(source)
    return { float32, sampleRate }
  } finally {
    try {
      await audioContext.close()
    } catch (e) {
      // ignore
    }
  }
}

// Prepares MicVAD model so that when the user starts recording we have it ready immediately.
export async function prepareVadModel() {
  detectSupportedAudioFormat()
  // Create and warm up a standalone AudioContext + AudioNodeVAD so that
  // we don't incur model-loading cost when the user starts recording.
  if (!vadAudioNodePromise) {
    vadAudioContext = new AudioContext({ sampleRate: 16000 })
    const { AudioNodeVAD } = await import('@ricky0123/vad-web')
    vadAudioNodePromise = AudioNodeVAD.new(vadAudioContext, {
      model: 'v5',
      baseAssetPath: '/vad/',
      onnxWASMBasePath: '/vad/',
      onSpeechEnd: async () => {
        await stopAndTranscribe()
      },
    })
    await vadAudioNodePromise
  }
  return vadAudioNodePromise
}

export async function startRecording(onSpeechStart, onSpeechStop, onTranscription, options = {}) {
  // If a previous session is still running, stop it first
  if (sessionActive) {
    await stopRecording()
  }
  sessionActive = true
  // Ensure we have detected a supported audio format even if VAD is disabled
  detectSupportedAudioFormat()

  const useVad = typeof options.useVad === 'boolean' ? options.useVad : true

  // Assign the session-specific implementation to the exported variable
  stopAndTranscribe = async () => {
    if (typeof onSpeechStop === 'function') {
      onSpeechStop()
    }

    // Stop the MediaRecorder and wait for it to finish
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      await new Promise((resolve) => {
        mediaRecorder.onstop = () => {
          resolve()
        }
        mediaRecorder.stop()
      })
    }

    await stopRecording()

    if (audioChunks && audioChunks.length > 0) {
      const audioBlob = new Blob(audioChunks, { type: detectedAudioFormat.mimeType })
      // If VAD is disabled, run an offline VAD gate. Early exit on first detected speech segment.
      if (!useVad) {
        try {
          const { float32, sampleRate } = await blobToFloat32AndSampleRate(audioBlob)
          const nrtVad = await prepareNonRealTimeVad()
          let speechDetected = false
          // Early stop as soon as any segment is produced
          // eslint-disable-next-line no-restricted-syntax
          for await (const segment of nrtVad.run(float32, sampleRate)) {
            if (segment) {
              speechDetected = true
              break
            }
          }
          if (!speechDetected) {
            onTranscription('', audioBlob)
            return
          }
        } catch (e) {
          // If offline VAD fails, fall back to transcribing
          // eslint-disable-next-line no-console
          console.warn('Offline VAD failed, proceeding to transcription', e)
        }
      }

      const transcription = await runTranscription(audioBlob)
      onTranscription(transcription, audioBlob)
    }
  }
  navigator.mediaDevices.getUserMedia({ audio: true })
    .then(async (stream) => {
      streamRef = stream

      if (typeof onSpeechStart === 'function') {
        onSpeechStart(stream)
      }

      // Clear any previously collected audio data
      audioChunks = []

      // Create MediaRecorder to capture audio
      const format = detectedAudioFormat || { mimeType: 'audio/webm' }
      mediaRecorder = new MediaRecorder(stream, { mimeType: format.mimeType })
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data)
        }
      }

      // Start recording
      mediaRecorder.start()

      if (useVad) {
        const audioNodeVAD = await prepareVadModel()

        if (vadAudioContext.state === 'suspended') {
          await vadAudioContext.resume()
        }

        // Hook the shared AudioNodeVAD up to this session's stream
        const sourceNode = vadAudioContext.createMediaStreamSource(stream)
        audioNodeVAD.receive(sourceNode)

        // Build MicVAD directly using the warmed components
        const { MicVAD } = await import('@ricky0123/vad-web')
        const vad = new MicVAD(
          audioNodeVAD.options,
          vadAudioContext,
          stream,
          audioNodeVAD,
          sourceNode,
        )

        vad.start()

        vadInstance = vad
      }
    })
    .catch((e) => {
      // eslint-disable-next-line no-console
      console.error('Failed to start recording:', e)
      store.commit('notifications/addError', {
        title: 'Could not access your microphone.',
      })
    })
  return {
    stopAndTranscribe,
  }
}
